import { useState, useEffect } from "react";
import { Card } from "../ui/Card";
import { Input } from "../ui/Input";
import { Plus, X, Info } from "lucide-react";
import { brevoTemplateService } from "../../services/brevoTemplateService";

interface TemplateParamsEditorProps {
  templateId: string;
  value: Record<string, any>;
  onChange: (params: Record<string, any>) => void;
}

const STANDARD_VARIABLES = ["firstName", "lastName", "email", "fullName"];

const extractVariables = (html: string): string[] => {
  if (!html) return [];

  // Match everything inside {{ }}
  const regex = /\{\{([\s\S]*?)\}\}/g;
  const matches = [...html.matchAll(regex)];

  if (!matches.length) return [];

  const unique = new Set<string>();

  matches.forEach((match) => {
    if (!match || !match[1]) return;
    let variable = match[1].trim();

    // Ignore Brevo standard variables and our own mapping
    if (
      variable.startsWith("contact.") ||
      variable.startsWith("user.") ||
      STANDARD_VARIABLES.includes(variable)
    ) {
      return;
    }

    // Strip "params." prefix if it exists, as Brevo expects flat keys in the params object
    if (variable.startsWith("params.")) {
      variable = variable.replace("params.", "");
    }

    if (variable && variable.length > 0) {
      unique.add(variable);
    }
  });

  return Array.from(unique);
};

export function TemplateParamsEditor({
  templateId,
  value,
  onChange,
}: TemplateParamsEditorProps) {
  const [detectedVariables, setDetectedVariables] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const loadTemplateDetails = async () => {
      if (!templateId) {
        setDetectedVariables([]);
        return;
      }

      setIsLoading(true);
      try {
        const response = await brevoTemplateService.getTemplateById(templateId);

        const variables = extractVariables(response.data?.htmlContent || "");
        setDetectedVariables(variables);
      } catch (error) {
        console.error("Failed to load template details:", error);
        setDetectedVariables([]);
      } finally {
        setIsLoading(false);
      }
    };
    loadTemplateDetails();
  }, [templateId]);

  const handleParamChange = (name: string, newValue: any) => {
    onChange({
      ...value,
      [name]: newValue,
    });
  };

  const handleAddCustomParam = () => {
    const newParamName = `custom_${Object.keys(value).length + 1}`;
    onChange({
      ...value,
      [newParamName]: "",
    });
  };

  const handleRemoveParam = (name: string) => {
    const newValue = { ...value };
    delete newValue[name];
    onChange(newValue);
  };

  const standardVariablesList = STANDARD_VARIABLES.map((v) => ({
    name: v,
    type: "standard" as const,
    required: false,
  }));

  const customParamsList = Object.entries(value).map(([key, val]) => ({
    name: key,
    value: val,
  }));

  if (!templateId) {
    return (
      <Card className="p-6 bg-gray-50 border-gray-200">
        <div className="flex items-center gap-2 text-[#989797]">
          <Info size={18} className="text-[#e07828]" />
          <p className="text-sm">Select a template to configure parameters</p>
        </div>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center gap-2">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-200 border-t-[#f09040]" />
          <p className="text-sm text-[#989797]">
            Loading template parameters...
          </p>
        </div>
      </Card>
    );
  }

  const allParams = [...standardVariablesList, ...customParamsList];

  if (allParams.length === 0) {
    return (
      <Card className="space-y-4">
        <div className="flex items-center gap-2 border-b border-gray-100 pb-3">
          <Info size={18} className="text-[#f09040]" />
          <h3 className="text-base font-semibold text-[#102040]">
            Template Parameters
          </h3>
        </div>
        <Card className="p-6 bg-[#FFF9E6] border-[#f09040]">
          <div className="flex items-start gap-2">
            <Info size={18} className="text-[#e07828] mt-0.5" />
            <div>
              <p className="text-sm font-medium text-[#102040]">
                No additional parameters required
              </p>
              <p className="text-xs text-[#989797] mt-1">
                This template only uses standard variables (firstName, lastName,
                email, fullName) which are automatically populated for each
                recipient.
              </p>
            </div>
          </div>
        </Card>
      </Card>
    );
  }

  return (
    <Card className="space-y-4">
      <div className="flex items-center gap-2 border-b border-gray-100 pb-3">
        <Info size={18} className="text-[#f09040]" />
        <h3 className="text-base font-semibold text-[#102040]">
          Template Parameters
        </h3>
      </div>

      <div className="space-y-4">
        {customParamsList.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-[#102040]">
                Custom Parameters ({customParamsList.length})
              </p>
            </div>
            {customParamsList.map((param) => (
              <div key={param.name} className="flex items-start gap-4">
                <div className="flex-1 space-y-1">
                  <label className="block text-xs font-medium text-[#989797]">
                    Parameter Key
                  </label>
                  <div className="relative">
                    <Input
                      type="text"
                      value={param.name}
                      onChange={(e) => {
                        const newKey = e.target.value.replace(
                          /[^a-zA-Z0-9_]/g,
                          "",
                        );
                        if (newKey === param.name) return;
                        const newValue = { ...value };
                        newValue[newKey] = newValue[param.name];
                        delete newValue[param.name];
                        onChange(newValue);
                      }}
                      placeholder="e.g., COUPON"
                      className="font-mono text-xs uppercase"
                    />
                  </div>
                </div>
                <div className="flex-2 space-y-1">
                  <div className="flex items-center justify-between">
                    <label className="block text-xs font-medium text-[#989797]">
                      Value
                    </label>
                    <button
                      type="button"
                      onClick={() => handleRemoveParam(param.name)}
                      className="p-1 hover:bg-gray-100 rounded transition-colors"
                      title="Remove parameter"
                    >
                      <X
                        size={14}
                        className="text-gray-400 hover:text-red-500"
                      />
                    </button>
                  </div>
                  <Input
                    type="text"
                    value={param.value}
                    onChange={(e) =>
                      handleParamChange(param.name, e.target.value)
                    }
                    placeholder="Enter value..."
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {detectedVariables.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-[#102040]">
                Detected Variables ({detectedVariables.length})
              </p>
            </div>
            {detectedVariables.map((variableName) => (
              <div key={variableName} className="space-y-1">
                <div>
                  <label className="block text-sm font-medium text-[#102040]">
                    {variableName} *
                  </label>
                  <Input
                    type="text"
                    value={value[variableName] || ""}
                    onChange={(e) =>
                      handleParamChange(variableName, e.target.value)
                    }
                    placeholder={`Value for ${variableName}`}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="pt-4 border-t border-gray-200 mt-4">
          <button
            type="button"
            onClick={handleAddCustomParam}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-dashed border-[#f09040] text-[#f09040] rounded-lg hover:bg-[#FFF9E6] transition-colors"
          >
            <Plus size={18} />
            <span className="text-sm font-medium">Add Custom Parameter</span>
          </button>
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <Info size={14} className="text-[#989797] mt-0.5" />
          <p className="text-xs text-[#989797]">
            <strong>Standard Variables</strong> (firstName, lastName, email,
            fullName) are automatically populated for each recipient and don't
            need to be configured here.
          </p>
        </div>
      </div>
    </Card>
  );
}
